import { debug } from "./log";
/**
 * Client-side image preparation for OCR upload — pre-compressed the moment a
 * file is selected so the "Search" button uploads an already-ready Blob.
 *
 *  • Longest side is downscaled to at most 640px, aspect-ratio preserved.
 *  • Small images are NEVER upscaled.
 *  • Encoded as JPEG at quality 0.78. If the result exceeds 300 KB we make ONE
 *    retry at 0.68 (no loop).
 *  • Target ≤ 250 KB, hard cap 300 KB — whichever attempt lands under 300 KB
 *    wins; if both attempts fail to reach the cap, the smaller Blob is
 *    returned (Backend still validates + re-encodes, so we never break
 *    submission).
 *  • Metadata (EXIF/ICC/etc.) is stripped naturally by canvas re-encoding.
 *  • Modern browsers auto-orient via EXIF at image-decode time, so the drawn
 *    frame is already upright.
 *  • Total budget capped at 5 seconds so a slow encoder cannot freeze the UI.
 *
 *  Returns a File named `medicine.jpg` (mime type image/jpeg).
 */

export const MAX_DIMENSION = 640;
export const TARGET_MAX_BYTES = 250 * 1024;
export const HARD_MAX_BYTES = 300 * 1024;
export const PRIMARY_QUALITY = 0.78;
export const RETRY_QUALITY = 0.68;
const HARD_TIMEOUT_MS = 5000;

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => resolve({ img, revoke: () => URL.revokeObjectURL(url) });
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("تعذّر قراءة الصورة"));
        };
        img.src = url;
    });
}

function canvasToBlob(canvas, quality) {
    return new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), "image/jpeg", quality);
    });
}

function drawToCanvas(img, srcW, srcH) {
    // Never upscale — only shrink when the longest side exceeds MAX_DIMENSION.
    let w = srcW;
    let h = srcH;
    const longest = Math.max(w, h);
    if (longest > MAX_DIMENSION) {
        const scale = MAX_DIMENSION / longest;
        w = Math.round(w * scale);
        h = Math.round(h * scale);
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    // JPEG has no alpha channel → flatten transparency to white so text stays
    // readable when the source is PNG/WEBP with an alpha layer.
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return { canvas, width: w, height: h };
}

/**
 * Two-shot encode: 0.78 → if > 300 KB then 0.68. No loop. Returns the
 * smaller/successful Blob and its final quality used.
 */
async function encodeTwoShot(canvas, deadline) {
    const first = await canvasToBlob(canvas, PRIMARY_QUALITY);
    if (!first) throw new Error("فشل ضغط الصورة");
    if (first.size <= HARD_MAX_BYTES) {
        return { blob: first, quality: PRIMARY_QUALITY, attempts: 1 };
    }
    if (performance.now() >= deadline) {
        // Out of time — return what we have (Backend will still validate).
        return { blob: first, quality: PRIMARY_QUALITY, attempts: 1, timedOut: true };
    }
    const second = await canvasToBlob(canvas, RETRY_QUALITY);
    if (!second) return { blob: first, quality: PRIMARY_QUALITY, attempts: 1 };
    // Keep whichever attempt is smaller — never enlarge the payload.
    return second.size < first.size
        ? { blob: second, quality: RETRY_QUALITY, attempts: 2 }
        : { blob: first, quality: PRIMARY_QUALITY, attempts: 2 };
}

/**
 * Prepare an arbitrary user-picked image (gallery or native camera capture).
 * The returned File is the exact bytes we hand to the upload — no further
 * client-side re-encode happens when the Search button is pressed.
 */
export async function compressForOcr(file) {
    const started = performance.now();
    const deadline = started + HARD_TIMEOUT_MS;
    const { img, revoke } = await loadImage(file);
    try {
        const { canvas, width, height } = drawToCanvas(img, img.naturalWidth, img.naturalHeight);
        const { blob, quality, attempts, timedOut } = await encodeTwoShot(canvas, deadline);
        const elapsed = Math.round(performance.now() - started);
        debug(
            `[ocr-prep] ${file.size}B → ${blob.size}B  ${img.naturalWidth}×${img.naturalHeight} → ${width}×${height}  q=${quality} attempts=${attempts}${timedOut ? " (timeout)" : ""}  ${elapsed}ms`
        );
        return new File([blob], "medicine.jpg", {
            type: "image/jpeg",
            lastModified: Date.now(),
        });
    } finally {
        revoke();
    }
}
